import React from 'react';

type Props = {
    TableData: Array,
    BorderColor: string,
    BorderWidth: string,
    BorderStyle: string,
    Fill: boolean,
    FillColor: string,
    HasHeader: boolean,
    HeaderHeight: number,
    RowHeight: number,
    ZebraStripe: boolean,
    AlternateColor: string
};

const Table = ({
    TableData = [],
    BorderColor = '#000000',
    BorderWidth = '1',
    BorderStyle = 'Solid',
    Fill = false,
    FillColor = 'transparent',
    HasHeader = true,
    HeaderHeight = 30,
    RowHeight = 25,
    ZebraStripe = false,
    AlternateColor = '#f5f5f5'
}: Props) => {
    
    const borderStyleMap = {
        'Solid': 'solid',
        'Dashed': 'dashed',
        'Dotted': 'dotted',
        'Double': 'double'
    };

    const tableStyle = {
        width: '100%',
        height: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'fixed'
    };

    const getCellStyle = (cell, isHeader, rowIndex) => {
        const baseStyle = {
            border: `${BorderWidth}px ${borderStyleMap[BorderStyle] || 'solid'} ${BorderColor}`,
            padding: '4px',
            textAlign: cell.align || 'left',
            color: cell.c || '#000000',
            backgroundColor: cell.bg || 'transparent',
            fontWeight: cell.bold ? 'bold' : 'normal',
            fontSize: `${cell.fontSize || 12}px`,
            height: isHeader ? `${HeaderHeight}px` : `${cell.h || RowHeight}px`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        };

        // Apply zebra striping
        if (ZebraStripe && !isHeader && rowIndex % 2 === 1) {
            baseStyle.backgroundColor = AlternateColor;
        }

        // Apply header styling
        if (isHeader) {
            baseStyle.fontWeight = 'bold';
            baseStyle.backgroundColor = '#f0f0f0';
        }

        return baseStyle;
    };

    const renderCellValue = (cell) => {
        if (cell.IsImage || cell.isImage) {
            return (
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    minHeight: 16,
                    border: '1px dashed #999',
                    color: '#666',
                    fontSize: 10
                }}>
                    {cell.v || cell.DataFieldName || 'Image'}
                </span>
            );
        }

        return cell.v || '';
    };

    const renderTable = () => {
        if (!TableData || TableData.length === 0) {
            return (
                <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `${BorderWidth}px ${borderStyleMap[BorderStyle] || 'solid'} ${BorderColor}`,
                    color: '#999'
                }}>
                    Table
                </div>
            );
        }

        return (
            <table style={tableStyle}>
                <tbody>
                    {TableData.map((row, rowIndex) => {
                        const isHeader = HasHeader && row.isHeader;
                        return (
                            <tr key={rowIndex}>
                                {row.Cells && row.Cells.map((cell, cellIndex) => {
                                    const cellStyle = getCellStyle(cell, isHeader, rowIndex);
                                    const TagName = isHeader ? 'th' : 'td';
                                    
                                    return (
                                        <TagName
                                            key={cellIndex}
                                            style={cellStyle}
                                            colSpan={cell.span || 1}
                                        >
                                            {renderCellValue(cell)}
                                        </TagName>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    return renderTable();
};

export default Table;
